import { datadogRum } from '@datadog/browser-rum';
import axios, { type AxiosError } from 'axios';
import { PagePaths } from '#app/shared/navigation/pagePaths';
import { getBearerHeader, resetAuthToken } from '#app/shared/utils/auth';

export const restClient = axios.create({
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFTOKEN',
});

restClient.interceptors.request.use(
  (config) => {
    if (!config || !config.headers) {
      return config;
    }

    const header = getBearerHeader();

    if (!header) {
      return config;
    }

    config.headers.Authorization = header;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

restClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      // no longer authenticated, redirect to sign-in with return URL
      datadogRum.addError(error);

      // Reset auth token and redirect to sign-in with current path as return URL
      resetAuthToken();
      const currentPath = window.location.pathname;

      // Prevent redirect loops by not setting returnTo if already on sign-in page
      const signInUrl =
        currentPath === PagePaths.SignIn
          ? PagePaths.SignIn
          : `${PagePaths.SignIn}?returnTo=${currentPath}`;
      window.location.href = signInUrl;

      return;
    }
    if (error?.response?.data) {
      let data = error.response.data;
      // Remove account related data from the logs
      if (error?.config?.url?.startsWith('/accounts')) {
        data = JSON.parse(JSON.stringify(data));
        if (data.form?.fields) {
          data.form.fields = {};
        }
      }
      // add the error to DataDog RUM
      datadogRum.addError(`${error} -> ${JSON.stringify(data)}`);
    } else {
      datadogRum.addError(error);
    }
    return Promise.reject(error);
  },
);

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const getErrorResponse = (error: any) => {
  if (!error) {
    return null;
  }
  const { response } = error;
  if (!error || !error.response) {
    return null;
  }
  return response as AxiosError;
};
